import type { InstrumentRow, InstrumentType, RequirementRow } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Box, Button, Group, LoadingOverlay, Popover, SegmentedControl, Stack, Switch, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, useState, type FC, type ReactElement } from 'react';

import { ApproveGate } from './ApproveGate';
import { dayLabel, localDateKey, whenLabel } from './dates';
import { groupDips, waitsOnLabel, type Dip } from './dips';
import classes from './DipTable.module.css';
import engineClasses from './engine.module.css';
import { blockedStub } from './partyRow';
import { CoverCell, LineStatus, QtyEditCell, SaveButton, VendorCell } from './PivotPanel';
import type { SkuTab } from './SkuWindow';
import { coverAfter, curveOf } from './stockStory';
import { Lines } from './tables';
import { useReplan } from './useReplan';

const n = (v: number, d = 0): string => formatCount(v, { maxDecimals: d });

type ActionKind = 'PRODUCTION' | 'MATERIAL' | 'FG' | 'TRANSFER';
type Filter = 'ALL' | ActionKind | 'CHOOSE';

const KIND_OF: Record<InstrumentType, ActionKind> = { WORK_ORDER: 'PRODUCTION', MATERIAL_PO: 'MATERIAL', FG_PO: 'FG', TRANSFER: 'TRANSFER' };
const PIN_LABEL: Record<ActionKind, [string, string]> = { PRODUCTION: ['Production', 'Production'], MATERIAL: ['RM / PM PO', 'RM / PM POs'], FG: ['FG PO', 'FG POs'], TRANSFER: ['STN', 'STNs'] };

type Row = { key: string; dip: Dip; head: RequirementRow; actions: InstrumentRow[]; own: InstrumentRow | null; blocked: boolean };

/** One pin per kind and day: "RM / PM PO · by 12 Nov"; a line waiting on a vendor gets its own pin with the pick in it. */
const ActionPins: FC<{ actions: InstrumentRow[]; filter: Filter; today: string; head: RequirementRow; windowStart: string | null; windowEnd: string | null; onChanged: () => void }> = ({ actions, filter, today, head, windowStart, windowEnd, onChanged }) => {
    const groups = useMemo(() => {
        const out = new Map<string, { kind: ActionKind; date: string; rows: InstrumentRow[]; choose: boolean; approved: boolean }>();
        for (const a of [...actions].sort((x, y) => x.placeBy.localeCompare(y.placeBy))) {
            const kind = KIND_OF[a.type];
            const choose = a.status === 'PROPOSED' && a.choicePending;
            const key = choose ? `choose:${a.id}` : `${kind}|${a.placeBy}|${a.status === 'PROPOSED' ? 'p' : 'a'}`;
            const g = out.get(key) ?? { kind, date: a.placeBy, rows: [], choose, approved: a.status !== 'PROPOSED' };
            g.rows.push(a);
            out.set(key, g);
        }
        return [...out.values()];
    }, [actions]);
    if (groups.length === 0) return <Text size="xs" c="dimmed">{head.landStatus === 'COVERED' ? 'nothing to place: covered' : 'no action'}</Text>;
    return (
        <div className={classes.pins}>
            {groups.map((g) => {
                const dim = filter !== 'ALL' && (filter === 'CHOOSE' ? !g.choose : g.kind !== filter);
                const [one, many] = PIN_LABEL[g.kind];
                const label = g.rows.length > 1 ? `${g.rows.length} ${many}` : one;
                const date = g.approved ? `${g.rows[0].status.toLowerCase()}${g.rows[0].documentCode ? ` · ${g.rows[0].documentCode}` : ''}` : g.date <= today ? 'by today' : `by ${dayLabel(g.date)}`;
                const pin = (
                    <span className={classes.pin} data-kind={g.kind} data-approved={g.approved ? 'true' : 'false'} data-choose={g.choose ? 'true' : undefined} data-dim={dim ? 'true' : undefined}>
                        <span className={classes.pinType}>{label}</span>
                        <span className={g.choose ? classes.choose : classes.pinDate}>{g.choose ? `choose ${g.kind === 'PRODUCTION' ? 'factory' : 'vendor'} · ${dayLabel(g.date)}` : date}</span>
                    </span>
                );
                return (
                    <Popover key={`${g.kind}|${g.date}|${g.rows[0].id}`} width={340} position="bottom-start" shadow="md" withinPortal>
                        <Popover.Target>
                            <span role="button" tabIndex={0} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                {pin}
                            </span>
                        </Popover.Target>
                        <Popover.Dropdown onClick={(e) => e.stopPropagation()}>
                            <Stack gap="xs">
                                {g.rows.map((r) => (
                                    <Stack key={r.id} gap={4}>
                                        <Lines lines={[{ text: `${r.itemName ?? r.itemId} · ${n(r.qty, r.unit ? 1 : 0)}${r.unit ? ` ${r.unit}` : ''}`, strong: true, sub: `${r.type === 'TRANSFER' ? `${r.fromFacilityId} → ${r.toFacilityId} · dispatch` : 'place'} by ${whenLabel(r.placeBy, today)} · lands ${dayLabel(r.landDate)}` }]} />
                                        {r.type !== 'TRANSFER' && <VendorCell r={r} req={head} today={today} window={{ start: windowStart, end: windowEnd }} onChanged={onChanged} />}
                                        <LineStatus r={r} />
                                    </Stack>
                                ))}
                            </Stack>
                        </Popover.Dropdown>
                    </Popover>
                );
            })}
        </div>
    );
};

/**
 * The SKUs that dip below their level in the objective's period. Left to right: the SKU;
 * its cover and run rate (the DRR opens how demand was read); when it dips and the earliest
 * day anything can land; what is short there and the most that can land then; the number
 * to replenish (edit, then Re-plan) with the cover it lands to; and the actions that make it
 * happen, as pins in the calendar's colours. A pin waiting on a vendor opens the pick.
 */
const DipTable: FC<{
    rows: RequirementRow[];
    loading: boolean;
    title: string;
    subtitle: string;
    windowStart: string | null;
    windowEnd: string | null;
    onOpenSku: (productId: string, tab?: SkuTab) => void;
    onApprove: (ids: string[]) => void;
    onSave?: (ids: string[]) => void;
    onChanged: () => void | Promise<void>;
}> = ({ rows, loading, title, subtitle, windowStart, windowEnd, onOpenSku, onApprove, onSave, onChanged }) => {
    const today = localDateKey();
    const [filter, setFilter] = useState<Filter>('ALL');
    const [showQuiet, setShowQuiet] = useState(false);
    const dips = useMemo(() => groupDips(rows), [rows]);
    // Per SKU, its curve and the dips in order: a later dip's cover is read with the earlier landings on it.
    const bySku = useMemo(() => {
        const out = new Map<string, Dip[]>();
        for (const d of dips) out.set(d.productId, [...(out.get(d.productId) ?? []), d].sort((a, b) => a.seq - b.seq));
        return out;
    }, [dips]);
    const all = useMemo<Row[]>(
        () =>
            dips.map((dip) => {
                const own = dip.parts[0] ? (dip.instruments.find((i) => i.id === dip.parts[0].instrumentId) ?? null) : null;
                const blocked = dip.parts.length === 0 && dip.head.landStatus !== 'COVERED' && dip.head.landStatus !== 'NO_DIP' && dip.head.landStatus !== 'UNSOURCEABLE';
                return { key: dip.key, dip, head: dip.head, actions: dip.instruments, own, blocked };
            }),
        [dips],
    );
    const isQuiet = (r: Row): boolean => r.dip.parts.length === 0 && !r.blocked;
    const quiet = all.filter(isQuiet).length;
    const visible = useMemo(() => {
        const base = showQuiet ? all : all.filter((r) => !isQuiet(r));
        if (filter === 'ALL') return base;
        return base.filter((r) => r.actions.some((a) => (filter === 'CHOOSE' ? a.status === 'PROPOSED' && a.choicePending : KIND_OF[a.type] === filter)));
    }, [all, showQuiet, filter]);
    const ownRows = useMemo(() => all.map((r) => r.own).filter((i): i is InstrumentRow => Boolean(i)), [all]);
    const blockedReqs = useMemo(() => all.filter((r) => r.blocked).map((r) => r.head), [all]);
    const { pinEdits, planning, bar } = useReplan({ rows: ownRows, blocked: blockedReqs, windowStart, windowEnd, onChanged });
    const counts = useMemo(() => {
        const c: Record<Filter, number> = { ALL: all.length, PRODUCTION: 0, MATERIAL: 0, FG: 0, TRANSFER: 0, CHOOSE: 0 };
        for (const r of all) {
            const kinds = new Set(r.actions.map((a) => KIND_OF[a.type]));
            for (const k of kinds) c[k] += 1;
            if (r.actions.some((a) => a.status === 'PROPOSED' && a.choicePending)) c.CHOOSE += 1;
        }
        return c;
    }, [all]);

    const columns = useMemo<DataSheetColumn<Row>[]>(
        () => [
            {
                key: 'sku',
                header: 'SKU',
                width: 230,
                sticky: true,
                sortable: true,
                valueAccessor: (r) => r.head.sku,
                render: (_v, r): ReactElement => <Lines lines={[{ text: r.head.sku, strong: true }, { text: '', sub: r.head.productName ?? '' }]} />,
            },
            {
                key: 'cover',
                header: 'Cover · DRR',
                width: 120,
                align: 'right',
                sortable: true,
                valueAccessor: (r) => r.head.coverDays ?? 0,
                render: (_v, r): ReactElement => <CoverCell req={r.head} onOpen={() => onOpenSku(r.head.productId, 'demand')} />,
            },
            {
                key: 'dates',
                header: 'Dips · earliest land',
                group: 'When and how much',
                width: 160,
                sortable: true,
                valueAccessor: (r) => r.head.dipDate ?? '',
                render: (_v, r): ReactElement => {
                    const h = r.head;
                    const late = Boolean(r.dip.firstLand && h.dipDate && r.dip.firstLand > h.dipDate);
                    const after = late && h.dipDate && r.dip.firstLand ? Math.round((new Date(`${r.dip.firstLand}T00:00:00`).getTime() - new Date(`${h.dipDate}T00:00:00`).getTime()) / 86400000) : 0;
                    return (
                        <Lines
                            lines={[
                                { text: `dips ${whenLabel(h.dipDate, today)}`, strong: true, late: Boolean(h.dipDate && h.dipDate <= today) },
                                {
                                    text: '',
                                    sub: h.landStatus === 'COVERED' ? `covered ${dayLabel(h.landDate)}` : r.dip.firstLand ? `earliest ${dayLabel(r.dip.firstLand)}${late ? ` · +${after}d` : ' · on time'}` : r.blocked ? 'nothing lands in the period' : '—',
                                    tone: h.stockoutDays > 0 ? 'red' : late ? 'orange' : h.landStatus === 'COVERED' ? 'green' : undefined,
                                },
                            ]}
                        />
                    );
                },
            },
            {
                key: 'short',
                header: 'Short',
                group: 'When and how much',
                width: 130,
                align: 'right',
                sortable: true,
                valueAccessor: (r) => r.head.requiredQty,
                render: (_v, r): ReactElement => {
                    const made = r.dip.parts.reduce((s, p) => s + p.qty, 0);
                    const h = r.head;
                    if (r.dip.parts.length === 0) return <Lines lines={[{ text: h.landStatus === 'COVERED' || h.landStatus === 'NO_DIP' ? '—' : n(h.requiredQty), strong: true, sub: h.landStatus === 'UNSOURCEABLE' ? 'no route' : h.landStatus === 'COVERED' ? 'on the book' : 'max possible 0', tone: r.blocked ? 'red' : undefined }]} />;
                    const waits = waitsOnLabel(r.dip.parts[0].gatingMaterialId, r.dip.parts[0].chain);
                    return <Lines lines={[{ text: n(h.requiredQty), strong: true, sub: made < h.requiredQty ? `max possible ${n(made)}${waits ? ` · ${waits}` : ''}` : `max possible ${n(made)}`, tone: made < h.requiredQty ? 'orange' : undefined }]} />;
                },
            },
            {
                key: 'replenish',
                header: 'Replenish',
                width: 170,
                align: 'right',
                render: (_v, r): ReactElement => {
                    const inst = r.own ?? (r.blocked ? blockedStub(r.head, r.head.route === 'BUY' ? 'FG_PO' : 'WORK_ORDER') : null);
                    if (!inst) return <Text size="sm" c="dimmed">—</Text>;
                    const edit = pinEdits.byId.get(inst.id);
                    const typed = edit && 'qty' in edit ? edit.qty : null;
                    const qty = typed ?? (r.blocked ? 0 : inst.qty);
                    const sku = bySku.get(r.head.productId) ?? [];
                    const before = sku.filter((d) => d.seq < r.dip.seq).flatMap((d) => d.parts.map((p) => ({ day: p.landDate, qty: p.qty })));
                    const later = r.dip.parts.slice(1).reduce((s, p) => s + p.qty, 0);
                    const land = r.dip.firstLand ?? r.head.landDate ?? r.head.dipDate;
                    const cover = qty > 0 ? coverAfter({ series: curveOf(sku.flatMap((d) => [d.head, ...d.parts])), onHand: r.head.onHand, before, landDate: land, qty: qty + later }) : null;
                    const sub = cover != null ? `replenishes to ${n(cover)} days of cover` : r.blocked ? 'type to make it anyway' : '';
                    return <QtyEditCell r={inst} edits={pinEdits} blank={r.blocked} sub={later > 0 ? `${sub} · +${n(later)} in later parts` : sub} />;
                },
            },
            {
                key: 'actions',
                header: 'Actions',
                width: 330,
                render: (_v, r): ReactElement => <ActionPins actions={r.actions} filter={filter} today={today} head={r.head} windowStart={windowStart} windowEnd={windowEnd} onChanged={() => void onChanged()} />,
            },
            {
                key: 'approve',
                header: '',
                width: 170,
                render: (_v, r): ReactElement => {
                    const open = r.actions.filter((a) => a.status === 'PROPOSED');
                    if (r.actions.length === 0) return <Text size="xs" c="dimmed">—</Text>;
                    if (open.length === 0) return <Text size="xs" c="teal">all approved</Text>;
                    return (
                        <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                            <ApproveGate>
                                <Button size="compact-xs" onClick={() => onApprove(open.map((a) => a.id))}>
                                    Review
                                </Button>
                            </ApproveGate>
                            {onSave && (
                                <Button size="compact-xs" variant="subtle" color="grape" onClick={() => onSave(open.map((a) => a.id))}>
                                    Save
                                </Button>
                            )}
                        </Group>
                    );
                },
            },
        ],
        [today, filter, pinEdits, bySku, windowStart, windowEnd, onOpenSku, onApprove, onSave, onChanged],
    );

    const openAll = all.flatMap((r) => r.actions.filter((a) => a.status === 'PROPOSED'));
    const uniqueOpen = [...new Map(openAll.map((a) => [a.id, a])).values()];
    const filterData = (
        [
            ['ALL', 'All'],
            ['PRODUCTION', 'Production'],
            ['MATERIAL', 'RM / PM POs'],
            ['FG', 'FG POs'],
            ['TRANSFER', 'STNs'],
            ['CHOOSE', 'Needs a vendor'],
        ] as Array<[Filter, string]>
    )
        .filter(([k]) => k === 'ALL' || counts[k] > 0)
        .map(([value, label]) => ({ value, label: value === 'ALL' ? label : `${label} · ${counts[value]}` }));

    return (
        <Stack gap="sm">
            {bar}
            <Box pos="relative">
                <LoadingOverlay visible={planning} zIndex={5} overlayProps={{ radius: 'sm', blur: 1 }} loaderProps={{ type: 'dots' }} />
                <DataSheet<Row>
                    data={visible}
                    columns={columns}
                    isLoading={loading}
                    getRowKey={(r) => r.key}
                    getRowClassName={(r) => (r.blocked ? engineClasses.blockedRow : '')}
                    rowHeight={60}
                    maxHeight="auto"
                    enableSearch
                    searchPlaceholder="Search SKU or name…"
                    title={title}
                    subtitle={subtitle}
                    onRowClick={(r) => onOpenSku(r.head.productId)}
                    toolbarControls={
                        <Group gap="sm" wrap="nowrap">
                            {filterData.length > 1 && <SegmentedControl size="xs" value={filter} onChange={(v) => setFilter(v as Filter)} data={filterData} />}
                            {quiet > 0 && <Switch size="xs" label={`show ${quiet} with nothing to do`} checked={showQuiet} onChange={(e) => setShowQuiet(e.currentTarget.checked)} />}
                        </Group>
                    }
                    toolbarActionsRight={
                        uniqueOpen.length > 0 ? (
                            <Group gap={4} wrap="nowrap">
                                <ApproveGate>
                                    <Button size="xs" variant="light" onClick={() => onApprove(uniqueOpen.map((a) => a.id))}>
                                        Review all {uniqueOpen.length}
                                    </Button>
                                </ApproveGate>
                                {onSave && <SaveButton open={uniqueOpen} windowStart={windowStart} windowEnd={windowEnd} onSave={onSave} all />}
                            </Group>
                        ) : undefined
                    }
                />
            </Box>
        </Stack>
    );
};

export default DipTable;
